import { useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const LETTERS = [
  { letter: "V", word: "Vision", desc: "Pursuing forward-thinking goals." },
  { letter: "A", word: "Ambition", desc: "Personal growth in every endeavor." },
  {
    letter: "L",
    word: "Leadership",
    desc: "Responsibility and positive influence within the community.",
  },
  { letter: "H", word: "Honor", desc: "Honesty and respect in all actions." },
  {
    letter: "U",
    word: "Unity",
    desc: "Collaborative environment where every individual belongs.",
  },
  {
    letter: "E",
    word: "Excellence",
    desc: "Committing to high standards in academics, arts, athletics, and character.",
  },
];

const gapFor = () => {
  const w = window.innerWidth;
  if (w < 640) return 26;
  if (w < 1024) return 56;
  return 96;
};

export const ValhueSection = () => {
  const sectionRef = useRef(null);
  const rowRef = useRef(null);
  const eyebrowRef = useRef(null);
  const letterRefs = useRef([]);
  const barRefs = useRef([]);
  const maskRefs = useRef([]);
  const wordRefs = useRef([]);
  const descRefs = useRef([]);
  const [active, setActive] = useState(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.set(eyebrowRef.current, { opacity: 0, y: 24 });
        gsap.set(maskRefs.current, { clipPath: "inset(0% 0% 100% 0%)" });
        gsap.set(wordRefs.current, { yPercent: 110 });
        gsap.set(descRefs.current, { opacity: 0, y: 10 });

        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top",
            end: "+=280%",
            scrub: 1,
            pin: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });

        tl.to(
          eyebrowRef.current,
          { opacity: 1, y: 0, duration: 0.25, ease: "power1.out" },
          0
        );
        tl.fromTo(
          rowRef.current,
          { gap: 0 },
          { gap: () => gapFor(), duration: 2.6 },
          0.1
        );

        LETTERS.forEach((_, i) => {
          const at = 0.55 + i * 0.38;
          tl.to(
            letterRefs.current[i],
            { scale: 1.14, color: "#FFFFFF", duration: 0.18 },
            at
          )
            .to(letterRefs.current[i], { scale: 1, duration: 0.22 }, at + 0.18)
            .to(barRefs.current[i], { scaleX: 1, duration: 0.22 }, at)
            .to(
              maskRefs.current[i],
              {
                clipPath: "inset(0% 0% 0% 0%)",
                duration: 0.34,
                ease: "power1.out",
              },
              at + 0.04
            )
            .to(
              wordRefs.current[i],
              { yPercent: 0, duration: 0.34, ease: "power1.out" },
              at + 0.04
            )
            .to(
              descRefs.current[i],
              { opacity: 1, y: 0, duration: 0.28 },
              at + 0.2
            );
        });

        tl.to({}, { duration: 0.5 });
      });

      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(rowRef.current, { gap: gapFor() });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#0A2647] text-white"
      data-testid="valhue-section"
      aria-label="The meaning of Val-Hue"
    >
      <div className="grain" aria-hidden="true" />

      <div className="relative flex h-[100dvh] flex-col items-center justify-center px-4">
        <p
          ref={eyebrowRef}
          className="mb-10 text-center text-[11px] font-semibold uppercase tracking-[0.45em] text-[#D6EBEE]/80 md:mb-16 md:text-xs"
        >
          The meaning of <span className="text-[#F47221]">Val-Hue</span>
        </p>

        <div
          ref={rowRef}
          className="flex items-start justify-center"
          style={{ gap: 0 }}
        >
          {LETTERS.map((item, i) => (
            <div
              key={item.letter}
              className="relative flex w-9 flex-col items-center sm:w-12 md:w-16 lg:w-20"
            >
              <button
                type="button"
                onClick={() => setActive(active === i ? null : i)}
                data-testid={`letter-${item.letter.toLowerCase()}`}
                aria-label={`${item.letter} for ${item.word}`}
                className="cursor-pointer lg:cursor-default"
              >
                <span
                  ref={(el) => (letterRefs.current[i] = el)}
                  className="block font-display text-4xl font-semibold leading-none text-[#D6EBEE] will-change-transform sm:text-5xl md:text-6xl lg:text-7xl"
                >
                  {item.letter}
                </span>
              </button>
              <span
                ref={(el) => (barRefs.current[i] = el)}
                className="mt-3 block h-[2px] w-5 origin-center scale-x-0 bg-[#F47221] md:mt-4 md:w-7"
                aria-hidden="true"
              />
              <div className="pointer-events-none absolute left-1/2 top-full mt-8 w-28 -translate-x-1/2 text-center md:mt-10 lg:w-[190px]">
                <div
                  ref={(el) => (maskRefs.current[i] = el)}
                  className="overflow-hidden"
                >
                  <span
                    ref={(el) => (wordRefs.current[i] = el)}
                    data-testid={`word-${item.word.toLowerCase()}`}
                    className="block text-[7px] font-semibold uppercase tracking-[0.12em] text-white sm:text-[9px] md:text-sm md:tracking-[0.3em]"
                  >
                    {item.word}
                  </span>
                </div>
                <p
                  ref={(el) => (descRefs.current[i] = el)}
                  className="mx-auto mt-2 hidden w-full text-balance text-[10px] leading-snug text-[#D6EBEE]/70 lg:block"
                >
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 h-20 lg:hidden" data-testid="letter-detail-panel">
          {active !== null && (
            <div className="text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F47221]">
                {LETTERS[active].letter} — {LETTERS[active].word}
              </p>
              <p className="mx-auto mt-2 max-w-[260px] text-balance text-[11px] leading-snug text-[#D6EBEE]/70">
                {LETTERS[active].desc}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
